import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { IconArrowLeft, IconMail, IconRefresh, IconSearch } from '../components/icons';
import { CopyButton, Dive, Empty, Skeleton, StatusPill, useAsync } from '../components/ui';
import { api, EmailDetail, EmailListItem, EmailStatus } from '../lib/api';
import { ago, bytes, customerEvents, dateTime, STATUS_HINT, STATUS_LABEL } from '../lib/format';
import { t } from '../lib/i18n';

const FILTERS: (EmailStatus | '')[] = ['', 'queued', 'sent', 'delivered', 'bounced', 'complained', 'failed', 'delivery_unknown', 'suppressed', 'cancelled'];

export function EmailsPage() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const stream = params.get('stream') ?? '';
  const [q, setQ] = useState(params.get('q') ?? '');
  const [items, setItems] = useState<EmailListItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const query = (c?: string) => {
    const p = new URLSearchParams({ limit: '25' });
    if (status) p.set('status', status);
    if (stream) p.set('stream', stream);
    if (params.get('q')) p.set('q', params.get('q')!);
    if (c) p.set('cursor', c);
    return `/emails?${p}`;
  };

  async function load(reset = true) {
    setLoading(true);
    try {
      const res = await api.get<{ items: EmailListItem[]; next_cursor: string | null }>(query(reset ? undefined : (cursor ?? undefined)));
      setItems((xs) => (reset ? res.items : [...xs, ...res.items]));
      setCursor(res.next_cursor);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const setParam = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p);
  };

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1>{t('Письма')}</h1>
          <p>{t('Каждое письмо и его путь — от приёма API до ответа почтового сервера.')}</p>
        </div>
        <button className="btn" onClick={() => load(true)}><IconRefresh size={15} /> {t('Обновить')}</button>
      </div>
      <div className="card">
        <div className="card-pad stack" style={{ borderBottom: '1px solid var(--border)' }}>
          <div className="row wrap">
            <form
              className="row"
              style={{ flex: 1, minWidth: 260 }}
              onSubmit={(e) => {
                e.preventDefault();
                setParam('q', q.trim());
              }}
            >
              <div style={{ position: 'relative', flex: 1 }}>
                <IconSearch size={16} style={{ position: 'absolute', left: 12, top: 12, color: 'var(--muted)' }} />
                <input className="input" style={{ paddingLeft: 36 }} placeholder={t('Получатель, тема или msg_…')} value={q} onChange={(e) => setQ(e.target.value)} />
              </div>
            </form>
            <select className="select input" style={{ width: 190 }} value={stream} onChange={(e) => setParam('stream', e.target.value)}>
              <option value="">{t('Все потоки')}</option>
              <option value="transactional">{t('Транзакционные')}</option>
              <option value="marketing">{t('Маркетинговые')}</option>
            </select>
          </div>
          <div className="chips">
            {FILTERS.map((f) => (
              <button key={f || 'all'} className={`chip${status === f ? ' on' : ''}`} onClick={() => setParam('status', f)} title={f ? t(STATUS_HINT[f]) : ''}>
                {f ? t(STATUS_LABEL[f]) : t('Все')}
              </button>
            ))}
          </div>
        </div>
        {loading && !items.length ? (
          <div className="card-pad stack">{Array.from({ length: 6 }, (_, i) => <Skeleton key={i} />)}</div>
        ) : !items.length ? (
          <Empty icon={<IconMail size={36} />} title={t('Ничего не найдено')}>{t('Попробуйте изменить фильтры или отправьте письмо через API.')}</Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th>{t('Статус')}</th><th>{t('Тема · отправитель')}</th><th>{t('Кому')}</th><th>{t('Поток')}</th><th>{t('Когда')}</th></tr>
              </thead>
              <tbody>
                {items.map((m) => (
                  <tr key={m.id} className="click" onClick={() => nav(`/emails/${m.id}`)}>
                    <td><StatusPill status={m.status} /></td>
                    <td className="truncate">
                      <strong>{m.subject}</strong>
                      <div className="small muted truncate">{m.from}</div>
                    </td>
                    <td className="truncate">{m.to.join(', ')}</td>
                    <td><span className="small muted">{m.stream === 'transactional' ? t('⚡ транз.') : t('🌊 маркет.')}</span></td>
                    <td className="muted small" title={dateTime(m.created_at)} style={{ whiteSpace: 'nowrap' }}>{ago(m.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {cursor && (
          <div className="card-pad" style={{ textAlign: 'center', borderTop: '1px solid var(--border)' }}>
            <button className="btn" onClick={() => load(false)} disabled={loading}>{loading ? t('Загружаем…') : t('Загрузить ещё')}</button>
          </div>
        )}
      </div>
    </>
  );
}

export function EmailDetailPage() {
  const { id = '' } = useParams();
  const { data: m, loading, reload } = useAsync(() => api.get<EmailDetail>(`/emails/${id}`), [id]);

  // Keep following the message while it is still in transit.
  useEffect(() => {
    if (m?.status !== 'queued') return;
    const t = setTimeout(reload, 1000);
    return () => clearTimeout(t);
  }, [m, reload]);

  if (loading && !m) return <div className="stack"><Skeleton h={32} w={360} /><Skeleton h={240} /></div>;
  if (!m) return <Empty title={t('Письмо не найдено')} />;
  const meta: [string, React.ReactNode][] = [
    ['ID', <span className="row"><code>{m.id}</code><CopyButton small value={m.id} label="" /></span>],
    [t('От'), `${m.from.name ? `${m.from.name} ` : ''}<${m.from.email}>`],
    [t('Кому'), m.to.join(', ')],
    ...(m.cc.length ? [[t('Копия'), m.cc.join(', ')] as [string, React.ReactNode]] : []),
    ...(m.bcc.length ? [[t('Скрытая копия'), m.bcc.join(', ')] as [string, React.ReactNode]] : []),
    ...(m.template ? [[t('Шаблон'), <Link to={`/templates/${m.template.id}`}>{t('версия {v}', { v: m.template.version })}</Link>] as [string, React.ReactNode]] : []),
    [t('Ответить'), m.reply_to ?? '—'],
    [t('Поток'), m.stream === 'transactional' ? t('Транзакционный') : t('Маркетинговый')],
    [t('Провайдер'), m.provider ? `${m.provider} · ${m.provider_message_id}` : '—'],
    [t('Принято'), dateTime(m.received_at)],
  ];
  return (
    <>
      <Link to="/emails" className="small row" style={{ marginBottom: 12 }}><IconArrowLeft size={14} /> {t('Все письма')}</Link>
      <div className="page-head">
        <div className="grow">
          <div className="row wrap">
            <h1>{m.subject}</h1>
            <StatusPill status={m.status} />
          </div>
          <p>{t(STATUS_HINT[m.status])}</p>
        </div>
        <button className="btn" onClick={reload}><IconRefresh size={15} /> {t('Обновить')}</button>
      </div>
      <div className="grid grid-2">
        <div className="card">
          <div className="card-head"><h3>{t('Погружение письма')}</h3><span className="small muted">{t('{n} событий', { n: customerEvents(m.timeline).length })}</span></div>
          <div className="card-pad"><Dive events={m.timeline} /></div>
        </div>
        <div className="stack">
          <div className="card">
            <div className="card-head"><h3>{t('Детали')}</h3></div>
            <div className="card-pad stack" style={{ gap: 10 }}>
              {meta.map(([k, v]) => (
                <div key={k}>
                  <div className="small muted">{k}</div>
                  <div style={{ wordBreak: 'break-all' }}>{v}</div>
                </div>
              ))}
            </div>
          </div>
          {m.attachments.length > 0 && (
            <div className="card">
              <div className="card-head"><h3>{t('Вложения')}</h3><span className="small muted">{m.attachments.length}</span></div>
              <div className="card-pad stack" style={{ gap: 8 }}>
                {m.attachments.map((a) => (
                  <div key={a.filename + a.size} className="row">
                    <span>📎</span>
                    <span className="truncate" style={{ flex: 1 }}><b>{a.filename}</b> <span className="small muted">{a.content_type} · {bytes(a.size)}</span></span>
                    {a.content_purged && <span className="pill suppressed" title={t('Содержимое удалено после отправки — храним только метаданные')}>{t('удалено')}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {m.suppressed_recipients.length > 0 && (
            <div className="callout warn">{t('Не отправлено подавленным адресам: {list}', { list: m.suppressed_recipients.join(', ') })}</div>
          )}
          {(Object.keys(m.tags).length > 0 || Object.keys(m.metadata).length > 0) && (
            <div className="card">
              <div className="card-head"><h3>{t('Теги и метаданные')}</h3></div>
              <div className="card-pad"><pre className="code">{JSON.stringify({ tags: m.tags, metadata: m.metadata }, null, 2)}</pre></div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
